import React from "react";
import { PageLayout } from "@/components/PageLayout";
import { FeedbackSection } from "@/components/FeedbackSection";
import { cn } from "@/lib/utils";
import { ToolUsageWrapper } from "@/components/ToolUsageWrapper";
import { toolPageClass } from "@/components/layout/toolContent";

interface PageLayoutProps {
  children: React.ReactNode;
  title: React.ReactNode;
  description: string;
  breadcrumbs?: { label: string; path: string }[];
  reliabilityLevel?: 1 | 2 | 3 | 4 | 5;
}

interface ToolLayoutProps extends Omit<PageLayoutProps, "children"> {
  children: React.ReactNode;
  toolName: string;
  feedbackSectionProps?: {
    enabled?: boolean;
  };
  maxWidth?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "6xl" | "7xl" | "full";
  badge?: React.ReactNode;
  className?: string;
}

const maxWidthClasses: Record<NonNullable<ToolLayoutProps["maxWidth"]>, string> = {
  sm: "lg:max-w-sm",
  md: "lg:max-w-md",
  lg: "lg:max-w-lg",
  xl: "lg:max-w-xl",
  "2xl": "lg:max-w-2xl",
  "3xl": "lg:max-w-3xl",
  "4xl": "lg:max-w-4xl",
  "5xl": "lg:max-w-5xl",
  "6xl": "lg:max-w-6xl",
  "7xl": "lg:max-w-7xl",
  full: "lg:max-w-full",
};

export const ToolLayout: React.FC<ToolLayoutProps> = ({
  children,
  toolName,
  feedbackSectionProps = { enabled: true },
  maxWidth = "full",
  badge,
  className,
  ...pageLayoutProps
}) => {
  const isFullPageWidth = maxWidth === "full";

  return (
    <PageLayout {...pageLayoutProps} fullWidth={isFullPageWidth}>
      <ToolUsageWrapper toolName={toolName}>
        <div
          className={cn(
            toolPageClass,
            "mx-auto relative",
            maxWidthClasses[maxWidth],
            className
          )}
        >
          {badge && (
            <div className="absolute top-0 right-0 z-10 max-w-[40%] sm:max-w-none">
              {badge}
            </div>
          )}
          {children}
        </div>
      </ToolUsageWrapper>

      {feedbackSectionProps.enabled && (
        <div className={cn(toolPageClass, "max-w-3xl mx-auto mt-6 sm:mt-10 px-1 sm:px-0")}>
          <FeedbackSection toolName={toolName} />
        </div>
      )}
    </PageLayout>
  );
};
